<template>
    <div class="container mx-auto px-4">
        <h1 class="font-serif mb-8 md:text-5xl text-2xl text-center">
            {{ __('Tranzakciók') }}
        </h1>

        <nav class="flex mb-8">
            <router-link class="bg-white border border-slate-200 cursor-pointer flex items-center justify-center -mr-px p-2 rounded-bl rounded-tl text-slate-800"
                         v-bind:to="{ name : 'payments' }"
            >
                <c-icon class="h-6 mr-2 stroke-slate-800 w-6" icon="calendar" />
                Heti bontás
            </router-link>
            <span class="bg-highlight border border-slate-200 cursor-pointer flex items-center justify-center -mr-px p-2 text-white">
                <c-icon class="h-6 mr-2 stroke-white w-6" icon="clipboard" />
                Ömlesztett
            </span>
        </nav>

        <div class="flex items-center mb-8">
            <c-control-text class="w-full" class-field="!h-10 !text-lg" class-group="bg-white border pl-2 rounded-none"
                            icon="search"
                            name="search"
                            v-bind:placeholder="__('Megrendelő keresése...')"
                            v-model="s"
                            v-on:keyup="search"
            />
        </div>

        <div v-if="orders.length > 0">
            <div class="mb-8" v-for="order in orders">
                <div class="bg-slate-100 border border-b-4 border-black flex font-bold mb-4 text-highlight text-sm">
                    <span class="border-black border-r p-2.5">
                        #{{ order.oid }} / {{ DateTime.fromISO(order.created_at).toFormat(localizationStore.dateTimeFormat) }}
                    </span>
                    <span class="border-black border-r p-2.5">
                        {{ order.user.prefix }}
                        <span class="uppercase">{{  order.user.lastname }}</span>
                        {{  order.user.firstname }}<br />
                        <small v-if="order.user.group">
                            ({{ __(':group csoport', { 'group' : order.user.group.name }) }})
                        </small>
                        <div class="flex items-center mt-2 text-slate-800" v-if="order.user.is_blacklisted">
                            <c-icon class="h-6 mr-2 stroke-error w-6" icon="check-circle" /> {{ __('Feketelistás') }}
                        </div>
                    </span>
                    <span class="ml-auto p-2.5 text-right" v-if="isRefundActive === 0">
                        {{ __('Rendelés összege') }}:
                        {{ currencyStore.format(order.amount_paid, 'huf') }}
                        <small class="block" v-if="order.ipn_status === 'FINISHED'">
                            <span class="text-slate-800">
                                (
                                <span v-if="!order.amount_refunded || order.amount_refunded === 0">{{ __('Fizetve') }}</span>
                                <span v-else>
                                    {{ currencyStore.format(order.amount_refunded, 'huf') }}
                                    {{ __('visszatérítve') }}
                                </span>
                                )
                            </span>
                        </small>
                    </span>
                    <c-button class="bg-highlight ml-auto mr-4 self-center text-white"
                              v-else
                              v-on:click="refund(order)"
                    >
                        {{ __('Visszatérítés') }}
                    </c-button>
                </div>
                <div>
                    <div v-for="product in order.items">
                        <div class="flex flex-col md:flex-row md:items-center mb-2 mr-4 pb-2">
                            <img class="hidden md:block mr-4 max-w-32 w-full"
                                 v-bind:alt="product.product.name"
                                 v-bind:src="product.product.picture"
                            />
                            <div class="mr-8">
                                <strong class="block text-slate-800 text-sm">{{ product.product.name }} <small>({{ __('days.' + product.day) }})</small></strong>
                                <span class="block max-w-96 text-slate-500 text-xs">{{ product.product.description }}</span>
                            </div>
                            <div class="flex items-center md:ml-auto" v-if="!refundedItems[order.id]?.[product.id]">
                                <strong class="mr-4">{{ product.quantity }}x</strong>
                                <span class="line-through mr-2" v-if="product.price_original !== product.price_gross">
                                    {{ currencyStore.format(product.price_original, 'huf') }}
                                </span>
                                <span>{{ currencyStore.format(product.price_gross, 'huf') }}</span>
                                <div class="flex flex-col items-end">
                                    <a class="cursor-pointer font-bold ml-4 text-xs"
                                       v-if="product.quantity > product.quantity_refunded"
                                       v-on:click="setRefundedItem(order.id, product.id)"
                                    >
                                        {{ __('Visszatérítés') }}
                                    </a>
                                    <span class="font-bold ml-4 text-xs"
                                          v-if="product.quantity_refunded > 0"
                                    >
                                        {{ __(':pcs db visszatérítve', { pcs: product.quantity_refunded }) }}
                                    </span>
                                </div>
                            </div>
                            <div class="md:ml-auto text-right text-xs" v-else>
                                {{ __('Visszatérítendő mennyiség') }}:
                                <div class="flex items-center">
                                    <c-control-number class="bg-white ml-auto mt-1 w-20"
                                                      v-bind:max="product.quantity - product.quantity_refunded"
                                                      v-bind:min="1"
                                                      v-bind:name="'refund_quantity.' + order.id + '.' + product.id"
                                                      v-model="refundedQuantities[order.id][product.id]"
                                    />
                                    <a class="cursor-pointer ml-4"
                                       v-on:click="unsetRefundedItem(order.id, product.id)"
                                    >
                                        ❌ {{ __('Mégsem') }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="border-t flex flex-col md:flex-row md:items-center mr-4 mt-2 pt-2"
                         v-if="order.value >= currencyStore.shipping_limits.zone && order.value < currencyStore.shipping_limits.free"
                    >
                        <div class="mr-4 max-w-32 w-full" />
                        <div class="mr-4">
                            <strong class="block text-slate-800 text-sm">{{ __('Kiszállítási költség') }}</strong>
                        </div>
                        <div class="md:ml-auto">
                            <strong class="mr-4">1x</strong>
                            <span>
                                {{ currencyStore.format(order.user.shipping_cost ?? currencyStore.shipping_costs[order.user.shipping_zone ?? 'default'], 'huf') }}
                            </span>
                        </div>
                    </div>
                    <div class="border-t flex flex-col md:flex-row md:items-center mr-4 mt-2 pt-2"
                         v-else
                    >
                        <div class="mr-4 max-w-32 w-full" />
                        <div class="mr-4">
                            <strong class="block text-slate-800 text-sm"
                                    v-if="order.value < currencyStore.shipping_limits.zone"
                            >
                                {{ __('Személyes átvétel') }}
                            </strong>
                            <strong class="block text-slate-800 text-sm"
                                    v-else
                            >
                                {{ __('Ingyenes szállítás') }}
                            </strong>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex items-center justify-center">
                <a class="flex border border-highlight cursor-pointer items-center justify-center leading-none rounded-l size-8 text-xl"
                   v-bind:class="{ 'border-slate-400 !cursor-not-allowed pointer-events-none text-slate-400' : currentPage === 1 }"
                   v-on:click="prevPage"
                >
                    <c-icon class="stroke-highlight size-4" icon="arrow-left"
                            v-bind:class="{ 'stroke-slate-400' : currentPage === 1 }"
                    />
                </a>
                <span class="flex bg-highlight border-y border-highlight items-center justify-center leading-none size-8 text-white">{{ currentPage }}</span>
                <a class="flex border border-highlight !cursor-pointer items-center justify-center leading-none rounded-r size-8 text-xl"
                   v-bind:class="{ 'border-slate-400 !cursor-not-allowed pointer-events-none text-slate-400' : currentPage === totalPages }"
                   v-on:click="nextPage"
                >
                    <c-icon class="stroke-highlight size-4" icon="arrow-right"
                            v-bind:class="{ 'stroke-slate-400' : currentPage === totalPages }"
                    />
                </a>
            </div>
        </div>

        <span class="block font-serif text-center text-2xl" v-else>{{ __('Nincs találat') }}</span>
    </div>
</template>

<script setup lang="ts">
    import { inject, onMounted, ref, toRaw } from 'vue';
    import {
        CButton,
        CIcon,
        CControlNumber,
        CControlText,
    } from '@teamfurther/cinderblock';
    import { DateTime } from 'luxon';
    import debounce from 'debounce';
    import OrderRepository from '../../repositories/OrderRepository';
    import { useCurrencyStore } from '../../stores/currency';
    import { useLocalizationStore } from '../../stores/localization';
    import { useRequestStore } from '../../stores/request';

    const __ = inject('__');
    const currencyStore = useCurrencyStore();
    const currentPage = ref(1);
    const isRefundActive = ref(0);
    const localizationStore = useLocalizationStore();
    const orderRepository = new OrderRepository();
    const orders = ref([]);
    const refundedItems = ref([]);
    const refundedQuantities = ref<Record<string, unknown>>({});
    const requestStore = useRequestStore();
    const s = ref<string>('');
    const totalPages = ref(1);

    async function getPayments() {
        const result = await orderRepository.index({
            filters: {
                is_webshop_order: true,
                user: s.value,
            },
            include: ['items', 'items.product', 'user', 'user.group'],
            page: currentPage.value,
            paginate: 1,
            sort: '-created_at',
        });

        currentPage.value = result.current_page;
        totalPages.value = result.last_page;

        orders.value = result.data;

        orders.value.forEach((order) => {
            order.items.forEach((item) => {
                if (!refundedQuantities.value[order.id]) {
                    refundedQuantities.value[order.id] = [];
                }

                refundedQuantities.value[order.id][item.id] = 0;
            });
        });
    }

    async function nextPage() {
        currentPage.value += 1;

        getPayments();
    }

    async function prevPage() {
        currentPage.value -= 1;

        getPayments();
    }

    async function refund(order) {
        requestStore.put('payments/refund/' + order.id, {
            quantities: toPlainDict(refundedQuantities.value[order.id]),
        }).then((response: any) => {
            isRefundActive.value = 0;
            refundedItems.value = [];

            getPayments();
        });
    }

    function setRefundedItem(orderId, productId) {
        isRefundActive.value++;

        if (!refundedItems.value[orderId]) {
            refundedItems.value[orderId] = [];
        }

        refundedItems.value[orderId][productId] = true;
    }

    const search = debounce(async () => {
        await getPayments();
    }, 500);

    function toPlainDict(arrLike: any) {
        return Object.fromEntries(
            Object.entries(toRaw(arrLike ?? {})).map(([k, v]) => {
                const n = Number(v);
                return [k, Number.isNaN(n) ? null : n];
            })
        );
    }

    function unsetRefundedItem(orderId, productId) {
        isRefundActive.value--;
        refundedItems.value[orderId][productId] = false;
        refundedQuantities.value[orderId][productId] = 0;
    }

    onMounted(async () => {
        await getPayments();
    });
</script>
